import { useState, useEffect, useMemo } from 'react';
import { useTemplateAnalyticsMV } from '../../../../TemplateManagerDashboard/hooks/useTemplateAnalyticsMV';
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_CHARS,
} from '../../../../../config/constants';
import { debugLog } from '../../../../../utils/debugLog';

// Normalize templates from analytics API into the shape expected by this dialog
const mapAnalyticsTemplatesToSelection = templates =>
  templates.map(template => ({
    id: template.template_id,
    template_id: template.template_id,
    name: template.template_name,
    template_name: template.template_name,
    category: template.template_category,
    template_category: template.template_category,
    description: template.template_description,
    // Preserve experiment information from API
    has_active_experiment: template.has_active_experiment || false,
    active_experiment: template.active_experiment || null,
    template_description: template.template_description,
    version: template.version_number,
    version_number: template.version_number,
    status: template.version_status || template.status,
    version_status: template.version_status || template.status,
    template: template.template,
    output_format: template.output_format,
    tags: Array.isArray(template.tags)
      ? template.tags
      : template.tags
        ? Object.values(template.tags)
        : [],
    created_at: template.created_at || template.version_created_at,
    ...template, // Include all other fields
  }));

/**
 * Custom hook for TemplateSelectionStep component
 *
 * Manages state and data fetching for template selection.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {string} props.selectedTemplateId - Pre-selected template ID
 * @returns {Object} Hook return object with state and functions
 */
export const useTemplateSelection = ({ open, selectedTemplateId }) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [templatesError, setTemplatesError] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  // Search state (debounced)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Debounce search query
  useEffect(() => {
    if (
      searchQuery.trim().length > 0 &&
      searchQuery.trim().length < SEARCH_MIN_CHARS
    ) {
      setIsDebouncing(true);
      const timer = setTimeout(() => {
        setDebouncedSearchQuery('');
        setIsDebouncing(false);
      }, SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    } else {
      setIsDebouncing(false);
      const timer = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery);
        // Reset to page 1 when search changes
        setCurrentPage(1);
      }, SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  // Fetch templates with analytics using server-side pagination via MV endpoint
  const trimmedSearchQuery = debouncedSearchQuery.trim();
  const hasValidSearchQuery = trimmedSearchQuery.length >= SEARCH_MIN_CHARS;

  const {
    templates: apiTemplates,
    loading,
    error: mvError,
    totalCount: apiTotalCount,
  } = useTemplateAnalyticsMV({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    category: categoryFilter !== 'all' ? categoryFilter : null,
    status: null, // Don't filter by status in API - fetch all and filter client-side
    searchQuery: hasValidSearchQuery ? trimmedSearchQuery : null,
    enabled: open, // Only fetch when dialog is open
  });

  // Filter for active + draft templates client-side (matching template list page default)
  const activeAndDraftTemplates = useMemo(() => {
    if (!apiTemplates || apiTemplates.length === 0) return [];

    return apiTemplates.filter(t => {
      const status = t.version_status || t.status;
      return status === 'active' || status === 'draft';
    });
  }, [apiTemplates]);

  // Map to expected format
  const allTemplates = useMemo(() => {
    return mapAnalyticsTemplatesToSelection(activeAndDraftTemplates);
  }, [activeAndDraftTemplates]);

  // Templates to display - use allTemplates (already filtered for active/draft)
  const displayTemplates = allTemplates;

  // Track loaded state
  useEffect(() => {
    if (open && !loading && apiTemplates) {
      setLoadedOnce(true);
      debugLog(
        `TemplateSelectionStep: Loaded ${apiTemplates.length} templates from API, ${activeAndDraftTemplates.length} active/draft templates`
      );
    }
  }, [open, loading, apiTemplates, activeAndDraftTemplates.length]);

  // Handle MV error
  useEffect(() => {
    if (mvError) {
      setTemplatesError(mvError);
    } else {
      setTemplatesError(null);
    }
  }, [mvError]);

  // Reset pagination when dialog opens or filters change
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open, categoryFilter]);

  // Create a map of template_id -> active experiment from templates data
  const templatesWithActiveExperiments = useMemo(() => {
    const map = new Map();
    allTemplates.forEach(template => {
      const templateId = template.template_id || template.id;
      if (
        template.has_active_experiment &&
        template.active_experiment &&
        templateId
      ) {
        map.set(templateId, template.active_experiment);
      }
    });
    return map;
  }, [allTemplates]);

  // Calculate total pages based on API total count
  const totalPages = Math.max(1, Math.ceil((apiTotalCount || 0) / pageSize));

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = newSize => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing size
  };

  // Set selected template if provided
  useEffect(() => {
    if (selectedTemplateId && displayTemplates.length > 0) {
      const template = displayTemplates.find(t => t.id === selectedTemplateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [selectedTemplateId, displayTemplates]);

  // Get unique categories from all templates
  const categories = useMemo(() => {
    return [...new Set(allTemplates.map(t => t.category))];
  }, [allTemplates]);

  // Filter by category (search already handles text filtering)
  const filteredTemplates = useMemo(() => {
    if (categoryFilter === 'all') {
      return displayTemplates;
    }
    return displayTemplates.filter(t => t.category === categoryFilter);
  }, [displayTemplates, categoryFilter]);

  return {
    // State
    searchQuery,
    categoryFilter,
    selectedTemplate,
    currentPage,
    pageSize,
    totalPages,
    templatesError,
    loading,
    loadedOnce,
    isDebouncing,
    filteredTemplates,
    categories,
    templatesWithActiveExperiments,
    apiTotalCount,

    // Actions
    setSearchQuery,
    setCategoryFilter,
    setSelectedTemplate,
    handlePrevPage,
    handleNextPage,
    handlePageSizeChange,
  };
};
